(ns ui.view.transaction-process.components.enrich-section
  "Phase 4b: the AI-enrichment block of the TP create modal.

   Once a template is chosen (the template defines the target entities), the user
   may drop a single PDF. The document is uploaded as a target-less attachment
   (no process exists yet), then `ui.enrich.event/request` enqueues a background
   job keyed to the chosen template + partial draft. This component only uploads,
   fires the request, and reflects the status of the job it started; the
   finished job and its review live in the top bar's process list
   (ui.view.enrich.processes)."
  (:require
   [common.localization :refer [tr]]
   [frontend.actions.attachment :as attachment-actions]
   [frontend.actions.common :as common-actions]
   [frontend.logging :as logging]
   [oops.core :as oops]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.model :as enrich-model]
   [ui.enrich.sub :as enrich-sub]
   [ui.util :as uu]
   [ui.view.enrich.fly :as fly]
   [ui.view.transaction-process.components.file-upload :as file-upload]))

(defn- upload-standalone!
  "Upload `file` as a target-less attachment and call `on-id` with its new id and
   filename. Enrichment reads the document before any entity exists, so the
   attachment hangs off the tenant, not a process."
  [{:keys [file on-id on-error]}]
  (let [filename (oops/oget file "name")]
    (common-actions/*<-create-op-token
     nil
     (fn [token]
       (attachment-actions/*=>add-attachment
        {:multipart-params {:filename    filename
                            :description nil
                            :file        file
                            :token       token}}
        (fn [resp] (on-id (get-in resp [:body :db/id]) filename))
        on-error))
     on-error)))

(defn- status-banner
  [{:keys [icon-class tone title hint]}]
  [:div {:class (uu/cm "flex items-start gap-2 rounded p-3 text-sm m-0"
                       (case tone
                         :info    "border border-primary-regular/30 bg-primary-regular/5 text-gray-700"
                         :error   "border border-danger-light-border bg-danger-light-bg text-danger-light-text"
                         "border border-gray-200 bg-gray-50 text-gray-700"))}
   [:i {:class (uu/cm "mt-0.5" icon-class)}]
   [:div {:class "flex flex-col"}
    [:span {:class "font-medium"} title]
    (when hint [:span {:class "text-xs m-0 opacity-80"} hint])]])

(defn section
  "Props:
   - `:template-kw` the chosen template (enrichment needs it).
   - `:draft-fn`    (fn []) returning the namespaced partial process data to send
                    as the resolver draft."
  [_props]
  (let [staged       (r/atom nil) ; {:attachment-id :filename} once uploaded
        uploading?   (r/atom false)
        upload-error (r/atom nil)
        token        (r/atom nil)] ; the job this section started
    (fn [{:keys [template-kw draft-fn]}]
      (let [job          @(rf/subscribe [::enrich-sub/by-token @token])
            in-progress? (enrich-model/in-progress-status? (:status job))
            enrich-error (enrich-model/failed-status? (:status job))]
        [:div {:class "flex flex-col gap-2 border-t border-gray-100 pt-3 mt-1"}
         [:div {:class "flex items-center gap-2"}
          [icons/sparkles-icon {:size 16 :class "text-primary-regular"}]
          [:span {:class "text-sm m-0 font-medium text-gray-700"}
           (tr :transaction-process/enrich-with-ai)]]
         [:p {:class "text-xs m-0 text-gray-500"}
          (tr :transaction-process/enrich-with-ai-hint)]

         (cond
           in-progress?
           [status-banner
            {:icon-class "fal fa-spinner-third fa-spin text-primary-regular"
             :tone       :info
             :title      (tr :transaction-process/enrich-in-progress)
             :hint       (tr :transaction-process/enrich-in-progress-hint)}]

           @uploading?
           [status-banner
            {:icon-class "fal fa-spinner-third fa-spin text-primary-regular"
             :tone       :info
             :title      (tr :transaction-process/enrich-uploading)}]

           @staged
           [:div {:class "flex flex-col gap-2"}
            [file-upload/staged-files
             {:files     [#js {:name (:filename @staged) :type "application/pdf"}]
              :on-remove (fn [_] (reset! staged nil))}]
            [button/primary-solid
             {:on-click (fn [e]
                          (reset! token (enrich-event/new-token))
                          (rf/dispatch
                           [::enrich-event/request
                            {:token          @token
                             :template-kw    template-kw
                             :draft          (draft-fn)
                             :rule-context   nil
                             :attachment-ids [(:attachment-id @staged)]}])
                          ;; Before the button goes, as the robot sets off from it.
                          (fly/to-processes! (dg/current-target e) @token)
                          (reset! staged nil))}
             (tr :transaction-process/enrich-start)]]

           :else
           [file-upload/drop-zone
            {:multiple? false
             :disabled? (nil? template-kw)
             :label     (tr :transaction-process/enrich-drop-pdf)
             :on-files  (fn [files]
                          (reset! upload-error nil)
                          (reset! uploading? true)
                          (upload-standalone!
                           {:file     (first files)
                            :on-id    (fn [id filename]
                                        (reset! uploading? false)
                                        (reset! staged {:attachment-id id :filename filename}))
                            :on-error (fn [err]
                                        (reset! uploading? false)
                                        (reset! upload-error true)
                                        (logging/error "Enrich upload failed" err))}))}])

         (when @upload-error
           [:div {:class "text-danger-light-text text-xs m-0"}
            (tr :transaction-process/enrich-upload-error)])
         (when (and enrich-error (not in-progress?))
           [:div {:class "text-danger-light-text text-xs m-0"}
            (tr (enrich-model/failure-tr-key job :transaction-process/enrich-failed))])]))))
